import { useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ordersApi, customersApi, inventoryApi, accessoriesApi } from '../api';
import { useFetch } from '../hooks/useFetch';
import toast from 'react-hot-toast';

const STATUS_FLOW = ['pending', 'stitching', 'qc', 'ready', 'delivered'];

function CreateOrderModal({ onClose, onSaved }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    customerId: '',
    phone: '',
    name: '',
    email: '',
    address: '',
    dueDate: '',
    notes: '',
    advancePayment: '',
    items: [],
    measurements: {
      shirt: { length: '', shoulder: '', upper_chest: '', middle_chest: '', lower_chest: '', sleeve_length: '', sleeve_bicep: '', sleeve_kaf: '', collar: '' },
      pant: { length: '', waist: '', thigh: '', calf: '', bottom: '' },
      pathani: { length: '', shoulder: '', upper_chest: '', middle_chest: '', lower_chest: '', sleeve_length: '', sleeve_bicep: '', sleeve_kaf: '', collar: '' },
      suit: { length: '', shoulder: '', upper_chest: '', middle_chest: '', lower_chest: '', sleeve_length: '', sleeve_bicep: '', sleeve_kaf: '', collar: '' },
      sherwani: { length: '', shoulder: '', upper_chest: '', middle_chest: '', lower_chest: '', sleeve_length: '', sleeve_bicep: '', sleeve_kaf: '', collar: '' },
      jacket: { length: '', shoulder: '', upper_chest: '', middle_chest: '', lower_chest: '', sleeve_length: '', sleeve_bicep: '', sleeve_kaf: '', collar: '' },
    }
  });
  const [itemForm, setItemForm] = useState({ inventoryId: '', templateId: '', garmentType: 'Shirt', clothQuantityUsed: '', sellingPrice: '' });
  const [customers, setCustomers] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [phoneSearch, setPhoneSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [tab, setTab] = useState("shirt");
  const garmentOptions = ["Shirt", "Pant", "Pathani", "Suit", "Sherwani", "jacket"];
  const filteredCustomers = customers.filter((c) =>
    c.phone?.includes(phoneSearch)
  );
  useEffect(() => {
    Promise.all([
      customersApi.getAll({ limit: 100 }),
      inventoryApi.getInventory({ limit: 100 }),
      accessoriesApi.getTemplates(),
    ]).then(([c, i, t]) => {
      setCustomers(c.data.data?.customers || c.data.data || []);
      setInventory(i.data.data?.inventory || i.data.data || []);
      setTemplates(t.data.data || []);
    });
  }, []);

  const addItem = () => {
    if (!itemForm.inventoryId || !itemForm.clothQuantityUsed || !itemForm.sellingPrice) {
      toast.error('Fill all item fields'); return;
    }
    const inv = inventory.find((i) => i.id === Number(itemForm.inventoryId));
    // Get the measurement for the specific garment type
    const garmentTypeLower = itemForm.garmentType.toLowerCase();
    const itemMeasurements = form.measurements[garmentTypeLower] || null;
    setForm((f) => ({ ...f, items: [...f.items, { ...itemForm, _inv: inv, measurements: itemMeasurements }] }));
    setItemForm({ inventoryId: '', templateId: '', garmentType: 'Shirt', clothQuantityUsed: '', sellingPrice: '' });
  };

  const handleSubmit = async () => {
    // ensure customer information is present
    if (!form.customerId && !form.name) { toast.error('Enter customer name'); return; }
    if (!form.phone) { toast.error('Enter customer phone'); return; }
    if (form.items.length === 0) { toast.error('Add at least one item'); return; }

    setLoading(true);
    try {
      let custId = form.customerId;
      if (!custId) {
        // create a new customer first
        const custPayload = {
          name: form.name,
          phone: form.phone,
          email: form.email || undefined,
          address: form.address || undefined,
          measurements: form.measurements,
        };
        const res = await customersApi.create(custPayload);
        custId = res.data.data?.customer?.id || res.data.data?.id;
      }

      const payload = {
        customerId: Number(custId),
        dueDate: form.dueDate || null,
        notes: form.notes,
        totalSellingPrice: form.items.reduce((s, i) => s + Number(i.sellingPrice), 0),
        advancePayment: form.advancePayment ? Number(form.advancePayment) : 0,
        items: form.items.map((i) => ({
          inventoryId: Number(i.inventoryId),
          templateId: i.templateId ? Number(i.templateId) : null,
          garmentType: i.garmentType,
          clothQuantityUsed: Number(i.clothQuantityUsed),
          sellingPrice: Number(i.sellingPrice),
          measurements: i.measurements || null,
        })),
      };
      await ordersApi.create(payload);
      toast.success('Order created!');
      // Update customer measurements if existing customer
      if (form.customerId) {
        await customersApi.update(form.customerId, { measurements: form.measurements });
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create order');
    }
    setLoading(false);
  };

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 680 }}>
        <div className="modal-header">
          <h3>Create New Order</h3>
          <button className="btn btn-secondary btn-sm btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {/* Customer & Details */}
          <div className="form-row form-row-2">
            <div className="form-group" style={{ position: 'relative' }}>
              <label className="form-label">Phone *</label>

              <input
                type="text"
                className="form-control"
                placeholder="Type phone number..."
                value={phoneSearch}
                onChange={(e) => {
                  const val = e.target.value;
                  setPhoneSearch(val);
                  setForm((f) => ({ ...f, phone: val }));
                  setShowDropdown(true);
                }}
                onFocus={() => setShowDropdown(true)}
              />

              {showDropdown && phoneSearch && (
                <div
                  style={{
                    position: 'absolute',
                    width: '100%',
                    background: '#fff',
                    border: '1px solid #ddd',
                    maxHeight: 150,
                    overflowY: 'auto',
                    zIndex: 10,
                    top: '100%',
                  }}
                >
                  {filteredCustomers.length === 0 ? (
                    <div style={{ padding: 8 }}>No customer found</div>
                  ) : (
                    filteredCustomers.map((c) => (
                      <div
                        key={c.id}
                        style={{ padding: 8, cursor: 'pointer' }}
                        onClick={() => {
                          setForm((prev) => ({
                            ...prev,
                            customerId: c.id,
                            phone: c.phone,
                            name: c.name,
                            email: c.email || '',
                            address: c.address || '',
                            measurements: {
                              ...prev.measurements,
                              ...c.measurements
                            }
                          }));

                          setPhoneSearch(c.phone);
                          setShowDropdown(false);
                        }}
                      >
                        {c.phone}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
            {/* Customer & Details */}
            <div className="form-group">
              <label className="form-label">Customer Name *</label>
              <input
                type="text"
                className="form-control"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Start typing phone to search or enter new name"
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Email </label>
              <input
                type="text"
                className="form-control"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="Customer email will appear here"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Address </label>
              <input
                type="text"
                className="form-control"
                value={form.address}
                onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                placeholder="Customer address will appear here"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Due Date</label>
              <input type="date" className="form-control" value={form.dueDate} onChange={(e) => setForm({ ...form, dueDate: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Advance Payment (₹)</label>
              <input type="number" className="form-control" min="0" step="0.01" value={form.advancePayment} onChange={(e) => setForm({ ...form, advancePayment: e.target.value })} placeholder="0" />
            </div>
          </div>
          <hr className="divider" />
          <p className="text-xs text-muted font-semibold mb-4" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>Body Measurements (inches)</p>
          {/* Tabs */}
          {/* <div style={{ display: "flex", gap: 10, marginBottom: 15 }}>
            <button type="button" onClick={() => setTab("shirt")} className={tab === "shirt" ? "btn btn-primary btn-sm" : "btn btn-secondary btn-sm"}>Shirt</button>
            <button type="button" onClick={() => setTab("pant")} className={tab === "pant" ? "btn btn-primary btn-sm" : "btn btn-secondary btn-sm"}>Pant</button>
            
          </div> */}
          <div className="form-group" style={{ marginBottom: 20 }}>
            <label className="form-label">Select Measurement Type</label>
            <select
              className="form-control"
              value={tab}
              onChange={(e) => setTab(e.target.value)}
            >
              {Object.keys(form.measurements).map((type) => (
                <option key={type} value={type}>
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </option>
              ))}
            </select>
          </div>

          {/* Shirt Measurements */}
          {tab === 'shirt' && (
            <div className="measurements-section shirt-measurements">
              <div className="measurement-row">
                <label htmlFor="">Length:</label>
                <input type="text" placeholder='Length' className="measurement-input" value={form.measurements.shirt.length || ""} onChange={(e) => setForm((f) => ({ ...f, measurements: { ...f.measurements, shirt: { ...f.measurements.shirt, length: e.target.value } } }))} />
              </div>
              <div className="measurement-row">
                <label htmlFor="">Shoulder:</label>
                <input type="text" placeholder='Shoulder' className="measurement-input" value={form.measurements.shirt.shoulder || ""} onChange={(e) => setForm((f) => ({ ...f, measurements: { ...f.measurements, shirt: { ...f.measurements.shirt, shoulder: e.target.value } } }))} />
              </div>
              <div className="measurement-row">
                <label htmlFor="">Chest:</label>
                <div className="measurement-inputs-group">
                  <input type="text" className="measurement-input" placeholder='upper chest' value={form.measurements.shirt.upper_chest || ""} onChange={(e) => setForm((f) => ({ ...f, measurements: { ...f.measurements, shirt: { ...f.measurements.shirt, upper_chest: e.target.value } } }))} />
                  <input type="text" className="measurement-input" placeholder='middle chest' value={form.measurements.shirt.middle_chest || ""} onChange={(e) => setForm((f) => ({ ...f, measurements: { ...f.measurements, shirt: { ...f.measurements.shirt, middle_chest: e.target.value } } }))} />
                  <input type="text" className="measurement-input" placeholder='lower chest' value={form.measurements.shirt.lower_chest || ""} onChange={(e) => setForm((f) => ({ ...f, measurements: { ...f.measurements, shirt: { ...f.measurements.shirt, lower_chest: e.target.value } } }))} />
                </div>
              </div>
              <div className="measurement-row">
                <label htmlFor="">Sleeve:</label>
                <div className="measurement-inputs-group">
                  <input type="text" className="measurement-input" placeholder='sleeve length' value={form.measurements.shirt.sleeve_length || ""} onChange={(e) => setForm((f) => ({ ...f, measurements: { ...f.measurements, shirt: { ...f.measurements.shirt, sleeve_length: e.target.value } } }))} />
                  <input type="text" className="measurement-input" placeholder='sleeve bicep' value={form.measurements.shirt.sleeve_bicep || ""} onChange={(e) => setForm((f) => ({ ...f, measurements: { ...f.measurements, shirt: { ...f.measurements.shirt, sleeve_bicep: e.target.value } } }))} />
                  <input type="text" className="measurement-input" placeholder='sleeve Kaf' value={form.measurements.shirt.sleeve_kaf || ""} onChange={(e) => setForm((f) => ({ ...f, measurements: { ...f.measurements, shirt: { ...f.measurements.shirt, sleeve_kaf: e.target.value } } }))} />
                </div>
              </div>
              <div className="measurement-row">
                <label htmlFor="">Collar:</label>
                <input type="text" className="measurement-input" placeholder='Collar' value={form.measurements.shirt.collar || ""} onChange={(e) => setForm((f) => ({ ...f, measurements: { ...f.measurements, shirt: { ...f.measurements.shirt, collar: e.target.value } } }))} />
              </div>
            </div>
          )}

          {/* Pant Measurements */}
          {tab === 'pant' && (
            <div className="measurements-section shirt-measurements">
              <div className="measurement-row">
                <label htmlFor="">Length:</label>
                <input type="text" placeholder='Length' className="measurement-input" value={form.measurements.pant.length || ""} onChange={(e) => setForm((f) => ({ ...f, measurements: { ...f.measurements, pant: { ...f.measurements.pant, length: e.target.value } } }))} />
              </div>
              <div className="measurement-row">
                <label htmlFor="">waist:</label>
                <input type="text" placeholder='waist' className="measurement-input" value={form.measurements.pant.waist || ""} onChange={(e) => setForm((f) => ({ ...f, measurements: { ...f.measurements, pant: { ...f.measurements.pant, waist: e.target.value } } }))} />
              </div>
              <div className="measurement-row">
                <label htmlFor="">Thigh:</label>
                <input type="text" className="measurement-input" placeholder='thigh' value={form.measurements.pant.thigh || ""} onChange={(e) => setForm((f) => ({ ...f, measurements: { ...f.measurements, pant: { ...f.measurements.pant, thigh: e.target.value } } }))} />
              </div>
              <div className="measurement-row">
                <label htmlFor="">Calf:</label>
                <input type="text" className="measurement-input" placeholder='Calf' value={form.measurements.pant.calf || ""} onChange={(e) => setForm((f) => ({ ...f, measurements: { ...f.measurements, pant: { ...f.measurements.pant, calf: e.target.value } } }))} />
              </div>
              <div className="measurement-row">
                <label htmlFor="">bottom:</label>
                <input type="text" className="measurement-input" placeholder='bottom' value={form.measurements.pant.bottom || ""} onChange={(e) => setForm((f) => ({ ...f, measurements: { ...f.measurements, pant: { ...f.measurements.pant, bottom: e.target.value } } }))} />
              </div>
            </div>
          )}

          {/* Pathani */}
          {tab === 'pathani' && (
            <div className="measurements-section shirt-measurements">
              <div className="measurement-row">
                <label htmlFor="">Length:</label>
                <input type="text" placeholder='Length' className="measurement-input" value={form.measurements.pathani.length || ""} onChange={(e) => setForm((f) => ({ ...f, measurements: { ...f.measurements, pathani: { ...f.measurements.pathani, length: e.target.value } } }))} />
              </div>
              <div className="measurement-row">
                <label htmlFor="">Shoulder:</label>
                <input type="text" placeholder='Shoulder' className="measurement-input" value={form.measurements.pathani.shoulder || ""} onChange={(e) => setForm((f) => ({ ...f, measurements: { ...f.measurements, pathani: { ...f.measurements.pathani, shoulder: e.target.value } } }))} />
              </div>
              <div className="measurement-row">
                <label htmlFor="">Chest:</label>
                <div className="measurement-inputs-group">
                  <input type="text" className="measurement-input" placeholder='upper chest' value={form.measurements.pathani.upper_chest || ""} onChange={(e) => setForm((f) => ({ ...f, measurements: { ...f.measurements, pathani: { ...f.measurements.pathani, upper_chest: e.target.value } } }))} />
                  <input type="text" className="measurement-input" placeholder='middle chest' value={form.measurements.pathani.middle_chest || ""} onChange={(e) => setForm((f) => ({ ...f, measurements: { ...f.measurements, pathani: { ...f.measurements.pathani, middle_chest: e.target.value } } }))} />
                  <input type="text" className="measurement-input" placeholder='lower chest' value={form.measurements.pathani.lower_chest || ""} onChange={(e) => setForm((f) => ({ ...f, measurements: { ...f.measurements, pathani: { ...f.measurements.pathani, lower_chest: e.target.value } } }))} />
                </div>
              </div>
              <div className="measurement-row">
                <label htmlFor="">Sleeve:</label>
                <div className="measurement-inputs-group">
                  <input type="text" className="measurement-input" placeholder='sleeve length' value={form.measurements.pathani.sleeve_length || ""} onChange={(e) => setForm((f) => ({ ...f, measurements: { ...f.measurements, pathani: { ...f.measurements.pathani, sleeve_length: e.target.value } } }))} />
                  <input type="text" className="measurement-input" placeholder='sleeve bicep' value={form.measurements.pathani.sleeve_bicep || ""} onChange={(e) => setForm((f) => ({ ...f, measurements: { ...f.measurements, pathani: { ...f.measurements.pathani, sleeve_bicep: e.target.value } } }))} />
                  <input type="text" className="measurement-input" placeholder='sleeve Kaf' value={form.measurements.pathani.sleeve_kaf || ""} onChange={(e) => setForm((f) => ({ ...f, measurements: { ...f.measurements, pathani: { ...f.measurements.pathani, sleeve_kaf: e.target.value } } }))} />
                </div>
              </div>
              <div className="measurement-row">
                <label htmlFor="">collar:</label>
                <input type="text" className="measurement-input" placeholder='Collar' value={form.measurements.pathani.collar || ""} onChange={(e) => setForm((f) => ({ ...f, measurements: { ...f.measurements, pathani: { ...f.measurements.pathani, collar: e.target.value } } }))} />
              </div>
            </div>
          )}

          {/* Suit */}
          {tab === 'suit' && (
            <div className="measurements-section shirt-measurements">
              <div className="measurement-row">
                <label htmlFor="">Length:</label>
                <input type="text" placeholder='Length' className="measurement-input" value={form.measurements.suit.length || ""} onChange={(e) => setForm((f) => ({ ...f, measurements: { ...f.measurements, suit: { ...f.measurements.suit, length: e.target.value } } }))} />
              </div>
              <div className="measurement-row">
                <label htmlFor="">Shoulder:</label>
                <input type="text" placeholder='Shoulder' className="measurement-input" value={form.measurements.suit.shoulder || ""} onChange={(e) => setForm((f) => ({ ...f, measurements: { ...f.measurements, suit: { ...f.measurements.suit, shoulder: e.target.value } } }))} />
              </div>
              <div className="measurement-row">
                <label htmlFor="">Chest:</label>
                <div className="measurement-inputs-group">
                  <input type="text" className="measurement-input" placeholder='upper chest' value={form.measurements.suit.upper_chest || ""} onChange={(e) => setForm((f) => ({ ...f, measurements: { ...f.measurements, suit: { ...f.measurements.suit, upper_chest: e.target.value } } }))} />
                  <input type="text" className="measurement-input" placeholder='middle chest' value={form.measurements.suit.middle_chest || ""} onChange={(e) => setForm((f) => ({ ...f, measurements: { ...f.measurements, suit: { ...f.measurements.suit, middle_chest: e.target.value } } }))} />
                  <input type="text" className="measurement-input" placeholder='lower chest' value={form.measurements.suit.lower_chest || ""} onChange={(e) => setForm((f) => ({ ...f, measurements: { ...f.measurements, suit: { ...f.measurements.suit, lower_chest: e.target.value } } }))} />
                </div>
              </div>
              <div className="measurement-row">
                <label htmlFor="">Sleeve:</label>
                <div className="measurement-inputs-group">
                  <input type="text" className="measurement-input" placeholder='sleeve length' value={form.measurements.suit.sleeve_length || ""} onChange={(e) => setForm((f) => ({ ...f, measurements: { ...f.measurements, suit: { ...f.measurements.suit, sleeve_length: e.target.value } } }))} />
                  <input type="text" className="measurement-input" placeholder='sleeve bicep' value={form.measurements.suit.sleeve_bicep || ""} onChange={(e) => setForm((f) => ({ ...f, measurements: { ...f.measurements, suit: { ...f.measurements.suit, sleeve_bicep: e.target.value } } }))} />
                  <input type="text" className="measurement-input" placeholder='sleeve Kaf' value={form.measurements.suit.sleeve_kaf || ""} onChange={(e) => setForm((f) => ({ ...f, measurements: { ...f.measurements, suit: { ...f.measurements.suit, sleeve_kaf: e.target.value } } }))} />
                </div>
              </div>
              <div className="measurement-row">
                <label htmlFor="">collar:</label>
                <input type="text" className="measurement-input" placeholder='Collar' value={form.measurements.suit.collar || ""} onChange={(e) => setForm((f) => ({ ...f, measurements: { ...f.measurements, suit: { ...f.measurements.suit, collar: e.target.value } } }))} />
              </div>
            </div>
          )}

          {/* Sherwani */}
          {tab === 'sherwani' && (
            <div className="measurements-section shirt-measurements">
              <div className="measurement-row">
                <label htmlFor="">Length:</label>
                <input type="text" placeholder='Length' className="measurement-input" value={form.measurements.sherwani.length || ""} onChange={(e) => setForm((f) => ({ ...f, measurements: { ...f.measurements, sherwani: { ...f.measurements.sherwani, length: e.target.value } } }))} />
              </div>
              <div className="measurement-row">
                <label htmlFor="">Shoulder:</label>
                <input type="text" placeholder='Shoulder' className="measurement-input" value={form.measurements.sherwani.shoulder || ""} onChange={(e) => setForm((f) => ({ ...f, measurements: { ...f.measurements, sherwani: { ...f.measurements.sherwani, shoulder: e.target.value } } }))} />
              </div>
              <div className="measurement-row">
                <label htmlFor="">Chest:</label>
                <div className="measurement-inputs-group">
                  <input type="text" className="measurement-input" placeholder='upper chest' value={form.measurements.sherwani.upper_chest || ""} onChange={(e) => setForm((f) => ({ ...f, measurements: { ...f.measurements, sherwani: { ...f.measurements.sherwani, upper_chest: e.target.value } } }))} />
                  <input type="text" className="measurement-input" placeholder='middle chest' value={form.measurements.sherwani.middle_chest || ""} onChange={(e) => setForm((f) => ({ ...f, measurements: { ...f.measurements, sherwani: { ...f.measurements.sherwani, middle_chest: e.target.value } } }))} />
                  <input type="text" className="measurement-input" placeholder='lower chest' value={form.measurements.sherwani.lower_chest || ""} onChange={(e) => setForm((f) => ({ ...f, measurements: { ...f.measurements, sherwani: { ...f.measurements.sherwani, lower_chest: e.target.value } } }))} />
                </div>
              </div>
              <div className="measurement-row">
                <label htmlFor="">Sleeve:</label>
                <div className="measurement-inputs-group">
                  <input type="text" className="measurement-input" placeholder='sleeve length' value={form.measurements.sherwani.sleeve_length || ""} onChange={(e) => setForm((f) => ({ ...f, measurements: { ...f.measurements, sherwani: { ...f.measurements.sherwani, sleeve_length: e.target.value } } }))} />
                  <input type="text" className="measurement-input" placeholder='sleeve bicep' value={form.measurements.sherwani.sleeve_bicep || ""} onChange={(e) => setForm((f) => ({ ...f, measurements: { ...f.measurements, sherwani: { ...f.measurements.sherwani, sleeve_bicep: e.target.value } } }))} />
                  <input type="text" className="measurement-input" placeholder='sleeve Kaf' value={form.measurements.sherwani.sleeve_kaf || ""} onChange={(e) => setForm((f) => ({ ...f, measurements: { ...f.measurements, sherwani: { ...f.measurements.sherwani, sleeve_kaf: e.target.value } } }))} />
                </div>
              </div>
              <div className="measurement-row">
                <label htmlFor="">collar:</label>
                <input type="text" className="measurement-input" placeholder='Collar' value={form.measurements.sherwani.collar || ""} onChange={(e) => setForm((f) => ({ ...f, measurements: { ...f.measurements, sherwani: { ...f.measurements.sherwani, collar: e.target.value } } }))} />
              </div>
            </div>
          )}

          {/* Jacket */}
          {tab === 'jacket' && (
            <div className="measurements-section shirt-measurements">
              <div className="measurement-row">
                <label htmlFor="">Length:</label>
                <input type="text" placeholder='Length' className="measurement-input" value={form.measurements.jacket.length || ""} onChange={(e) => setForm((f) => ({ ...f, measurements: { ...f.measurements, jacket: { ...f.measurements.jacket, length: e.target.value } } }))} />
              </div>
              <div className="measurement-row">
                <label htmlFor="">Shoulder:</label>
                <input type="text" placeholder='Shoulder' className="measurement-input" value={form.measurements.jacket.shoulder || ""} onChange={(e) => setForm((f) => ({ ...f, measurements: { ...f.measurements, jacket: { ...f.measurements.jacket, shoulder: e.target.value } } }))} />
              </div>
              <div className="measurement-row">
                <label htmlFor="">Chest:</label>
                <div className="measurement-inputs-group">
                  <input type="text" className="measurement-input" placeholder='upper chest' value={form.measurements.jacket.upper_chest || ""} onChange={(e) => setForm((f) => ({ ...f, measurements: { ...f.measurements, jacket: { ...f.measurements.jacket, upper_chest: e.target.value } } }))} />
                  <input type="text" className="measurement-input" placeholder='middle chest' value={form.measurements.jacket.middle_chest || ""} onChange={(e) => setForm((f) => ({ ...f, measurements: { ...f.measurements, jacket: { ...f.measurements.jacket, middle_chest: e.target.value } } }))} />
                  <input type="text" className="measurement-input" placeholder='lower chest' value={form.measurements.jacket.lower_chest || ""} onChange={(e) => setForm((f) => ({ ...f, measurements: { ...f.measurements, jacket: { ...f.measurements.jacket, lower_chest: e.target.value } } }))} />
                </div>
              </div>
              <div className="measurement-row">
                <label htmlFor="">Sleeve:</label>
                <div className="measurement-inputs-group">
                  <input type="text" className="measurement-input" placeholder='sleeve length' value={form.measurements.jacket.sleeve_length || ""} onChange={(e) => setForm((f) => ({ ...f, measurements: { ...f.measurements, jacket: { ...f.measurements.jacket, sleeve_length: e.target.value } } }))} />
                  <input type="text" className="measurement-input" placeholder='sleeve bicep' value={form.measurements.jacket.sleeve_bicep || ""} onChange={(e) => setForm((f) => ({ ...f, measurements: { ...f.measurements, jacket: { ...f.measurements.jacket, sleeve_bicep: e.target.value } } }))} />
                  <input type="text" className="measurement-input" placeholder='sleeve Kaf' value={form.measurements.jacket.sleeve_kaf || ""} onChange={(e) => setForm((f) => ({ ...f, measurements: { ...f.measurements, jacket: { ...f.measurements.jacket, sleeve_kaf: e.target.value } } }))} />
                </div>
              </div>
              <div className="measurement-row">
                <label htmlFor="">collar:</label>
                <input type="text" className="measurement-input" placeholder='Collar' value={form.measurements.jacket.collar || ""} onChange={(e) => setForm((f) => ({ ...f, measurements: { ...f.measurements, jacket: { ...f.measurements.jacket, collar: e.target.value } } }))} />
              </div>
            </div>
          )}



          <div className="form-group mt-4">
            <label className="form-label">Notes</label>
            <textarea className="form-control" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Special instructions..." />
          </div>

          {/* Add Items */}
          <hr className="divider" />
          <h4 className="font-semibold text-sm text-muted mb-4" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>Add Garments</h4>

          <div className="form-row form-row-2">
            <div className="form-group">
              <label className="form-label">Cloth Inventory *</label>
              <select className="form-control" value={itemForm.inventoryId} onChange={(e) => {
                  const selectedInv = inventory.find(i => i.id === Number(e.target.value));
                  setItemForm({ 
                    ...itemForm, 
                    inventoryId: e.target.value,
                    sellingPrice: selectedInv?.lot?.sellingPricePerUnit || ''
                  });
                }}>
                <option value="">Select Cloth</option>
                {inventory.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.lot?.lotNumber} – {i.clothType?.replace('_', ' ')} ({Number(i.quantityAvailable).toFixed(1)} {i.lot?.unit})
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Garment Type *</label>

              <select
                className="form-control"
                value={itemForm.garmentType}
                onChange={(e) =>
                  setItemForm({ ...itemForm, garmentType: e.target.value })
                }
              >
                <option value="">Select Garment</option>

                {garmentOptions.map((garment) => (
                  <option key={garment} value={garment}>
                    {garment}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-row form-row-3">
            <div className="form-group">
              <label className="form-label">Qty Used *</label>
              <input type="number" className="form-control" step="0.01" min="0.01" value={itemForm.clothQuantityUsed} onChange={(e) => setItemForm({ ...itemForm, clothQuantityUsed: e.target.value })} placeholder="2.5" />
            </div>
            <div className="form-group">
              <label className="form-label">Selling Price (₹) *</label>
              <input type="number" className="form-control" step="0.01" min="0" value={itemForm.sellingPrice} onChange={(e) => setItemForm({ ...itemForm, sellingPrice: e.target.value })} placeholder="1200" />
            </div>
            <div className="form-group">
              <label className="form-label">Accessory Template</label>
              <select className="form-control" value={itemForm.templateId} onChange={(e) => setItemForm({ ...itemForm, templateId: e.target.value })}>
                <option value="">None</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          </div>
          <button type="button" className="btn btn-secondary w-full" onClick={addItem}>+ Add Item</button>

          {/* Items List */}
          {form.items.length > 0 && (
            <div className="mt-4">
              <table style={{ fontSize: '0.85rem' }}>
                <thead>
                  <tr>
                    <th>Garment</th>
                    <th>Cloth</th>
                    <th>Qty</th>
                    <th>Price</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {form.items.map((item, idx) => (
                    <tr key={idx}>
                      <td className="font-semibold">{item.garmentType}</td>
                      <td className="text-muted text-xs">{item._inv?.lot?.lotNumber}</td>
                      <td>{item.clothQuantityUsed}</td>
                      <td className="text-success font-semibold">₹{Number(item.sellingPrice).toLocaleString('en-IN')}</td>
                      <td><button className="btn btn-danger btn-sm" onClick={() => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }))}>×</button></td>
                    </tr>
                  ))}
                  <tr>
                    <td colSpan={3} className="font-bold">Total</td>
                    <td className="font-bold text-success">₹{form.items.reduce((s, i) => s + Number(i.sellingPrice), 0).toLocaleString('en-IN')}</td>
                    <td></td>
                  </tr>
                  {form.advancePayment > 0 && (
                    <tr>
                      <td colSpan={3} className="font-bold">Advance Paid</td>
                      <td className="font-bold text-primary">₹{Number(form.advancePayment).toLocaleString('en-IN')}</td>
                      <td></td>
                    </tr>
                  )}
                  {form.advancePayment > 0 && (
                    <tr>
                      <td colSpan={3} className="font-bold">Balance</td>
                      <td className="font-bold text-danger">₹{(form.items.reduce((s, i) => s + Number(i.sellingPrice), 0) - Number(form.advancePayment)).toLocaleString('en-IN')}</td>
                      <td></td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={loading || form.items.length === 0}>
            {loading ? 'Creating...' : `Create Order (${form.items.length} items)`}
          </button>
        </div>
      </div>
    </div>

  );
}

export default function Orders() {
  const [modal, setModal] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);

  // Get current date for due date comparison
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Check if due date is within 3 days (today + 3 days)
  const isDueSoon = (dueDate) => {
    if (!dueDate) return false;
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays >= 0 && diffDays <= 3;

  };

  const fetchOrders = useCallback(() => ordersApi.getAll({ search, status: statusFilter, page, limit: 20 }), [search, statusFilter, page]);
  const { data, loading, refetch } = useFetch(fetchOrders, [search, statusFilter, page]);
  const orders = data?.orders || data || [];
  const pagination = data?.pagination;

  const updateStatus = async (id, status) => {
    try {
      await ordersApi.updateStatus(id, status);
      toast.success('Status updated');
      refetch();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Orders</h1>
        <button className="btn btn-primary" onClick={() => setModal(true)}>+ New Order</button>
      </div>

      <div className="filters-bar">
        <div className="search-input" style={{ flex: 1, minWidth: 200 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
          <input className="form-control" placeholder="Search order number..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
        </div>
        <select className="form-control" style={{ width: 160 }} value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="">All Status</option>
          {STATUS_FLOW.map((s) => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      <div className="card">
        {loading ? (
          <div className="empty-state"><div className="spinner" /></div>
        ) : (
          <div className="table-wrapper" style={{ border: 'none', borderRadius: 0 }}>
            <table>
              <thead>
                <tr>
                  <th>Order #</th>
                  <th>Customer</th>
                  {/* <th>Items</th> */}
                  <th>Status</th>
                  <th>Total</th>
                  <th>Advance</th>
                  <th>Balance</th>
                  <th>Profit</th>
                  <th>Created At</th>
                  <th>Due Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.length === 0 ? (
                  <tr><td colSpan={11}><div className="empty-state"><div className="empty-icon">📋</div><p>No orders found</p></div></td></tr>
                ) : orders.map((order) => {
                  const idx = STATUS_FLOW.indexOf(order.status);
                  const nextStatus = STATUS_FLOW[idx + 1];
                  return (
                    <tr key={order.id}>
                      <td><Link to={`/orders/${order.id}`} className="mono text-sm" style={{ color: 'var(--accent-light)' }}>{order.orderNumber}</Link></td>
                      <td>
                        <div className="font-semibold text-sm">{order.customer?.name}</div>
                        <div className="text-xs text-muted">{order.customer?.phone}</div>
                      </td>
                      {/* <td><span className="badge badge-qc">{order.items?.length || 0} items</span></td> */}
                      <td><span className={`badge badge-${order.status}`}>{order.status}</span></td>
                      <td className="font-semibold">₹{Number(order.totalSellingPrice).toLocaleString('en-IN')}</td>
                      <td className="font-semibold text-primary">{Number(order.advancePayment || order.advance_payment || 0) > 0 ? `₹${Number(order.advancePayment || order.advance_payment || 0).toLocaleString('en-IN')}` : '—'}</td>
                      <td className="font-semibold text-danger">{Number(order.advancePayment || order.advance_payment || 0) > 0 ? `₹${(Number(order.totalSellingPrice) - Number(order.advancePayment || order.advance_payment || 0)).toLocaleString('en-IN')}` : '—'}</td>
                      <td className={`font-semibold ${Number(order.profit) >= 0 ? 'text-success' : 'text-danger'}`}>
                        ₹{Number(order.profit).toLocaleString('en-IN')}
                      </td>
                      <td className="text-sm text-muted">{order.createdAt ? new Date(order.createdAt).toLocaleDateString('en-IN') : '—'}</td>
                      <td className={`text-sm text-muted ${isDueSoon(order.dueDate) ? 'text-danger font-semibold' : ''}`}>{order.dueDate || '—'}</td>
                      <td>
                        <div className="flex gap-2">
                          <Link to={`/orders/${order.id}`} className="btn btn-secondary btn-sm">View</Link>
                          {nextStatus && order.status !== 'cancelled' && (
                            <button className="btn btn-success btn-sm" onClick={() => updateStatus(order.id, nextStatus)}>
                              → {nextStatus}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {pagination && (
          <div className="pagination">
            <span>Page {pagination.page} of {pagination.totalPages} ({pagination.total} orders)</span>
            <div className="pagination-btns">
              <button className="btn btn-secondary btn-sm" disabled={!pagination.hasPrev} onClick={() => setPage(p => p - 1)}>← Prev</button>
              <button className="btn btn-secondary btn-sm" disabled={!pagination.hasNext} onClick={() => setPage(p => p + 1)}>Next →</button>
            </div>
          </div>
        )}
      </div>

      {modal && <CreateOrderModal onClose={() => setModal(false)} onSaved={refetch} />}
    </div>
  );
}
